import React, { useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import {
  LuArrowDownWideNarrow,
  LuArrowUpDown,
  LuBuilding2,
  LuCalendarClock,
  LuDatabase,
  LuFilter,
  LuPlus,
  LuSearch,
} from "react-icons/lu";
import { toast } from "react-toastify";

import AppLayout from "../../components/layout/AppLayout";
import Breadcrumb from "../../components/layout/Breadcrumb";
import TurningReceiveList from "../../components/turningReceives/TurningReceiveList";
import Pagination from "../../components/pagination";
import Button from "../../components/ui/Button";
import FilterSelect, { FilterOption } from "../../components/ui/FilterSelect";
import Input from "../../components/ui/Input";
import cx from "../../components/ui/cx";
import {
  actionBarEndClass,
  cardClass,
  cardContentClass,
  cardHeaderClass,
  cardTitleClass,
  fieldIconClass,
  fieldInputClass,
  filterFooterClass,
  filterGridClass,
  filterLabelClass,
  pageTitleClass,
} from "../../components/ui/styles";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import {
  fetchTurningReceiveFilterParties,
  fetchTurningReceiveListData,
  getTurningReceiveFilterPartyOptions,
  getTurningReceives,
  getTotalTurningReceives,
} from "../../features/turningReceives/turningReceivesSlice";
import {
  getListState,
  saveListState,
} from "../../features/listState/listStateSlice";
import { getLoggingUser } from "../../features/loggingUser/loggingUserSlice";
import { updatePdfFile } from "../../features/settings/settingsSlice";
import { deleteTurningReceive } from "../../services/turningReceive.service";
import {
  checkReportRange,
  makeReport,
} from "../../services/download.service";
import { hasPermission, permission } from "../../static/permission";
import { route } from "../../static/route";
import { toastStyles } from "../../static/styles";
import { BreadcrumbType } from "../../types/common";
import {
  TurningReceiveDateBy,
  TurningReceiveSortBy,
  TurningReceiveSortOrder,
} from "../../types/turningReceive";

const breadcrumbItems: BreadcrumbType[] = [
  { label: "હોમ", href: route.dashboard },
  { label: "ટર્નિંગ રિસીવ" },
];

const listKey = "turningReceives";

interface FilterFormInput {
  search: string;
  fromDate: string;
  toDate: string;
}

type SelectFilters = {
  party_id: number | null;
  date_by: TurningReceiveDateBy;
  sort_by: TurningReceiveSortBy;
  sort_order: TurningReceiveSortOrder;
};

type Filters = FilterFormInput & SelectFilters;

// Newest receive date first, until the user picks another order.
const defaultSelects: SelectFilters = {
  party_id: null,
  date_by: "date",
  sort_by: "date",
  sort_order: "desc",
};

const noFilters: Filters = {
  search: "",
  fromDate: "",
  toDate: "",
  ...defaultSelects,
};

// The filter card's selects within a set of filters.
const selectsOf = (filters: Filters): SelectFilters => {
  const { search, fromDate, toDate, ...selects } = filters;

  return selects;
};

const dateByOptions: FilterOption<TurningReceiveDateBy>[] = [
  { value: "date", label: "તારીખ" },
  { value: "created_at", label: "બનાવ્યા તારીખ" },
];

const sortByOptions: FilterOption<TurningReceiveSortBy>[] = [
  { value: "id", label: "ID" },
  { value: "date", label: "તારીખ" },
  { value: "created_at", label: "બનાવ્યા તારીખ" },
  { value: "updated_at", label: "સુધાર્યા તારીખ" },
];

const sortOrderOptions: FilterOption<TurningReceiveSortOrder>[] = [
  { value: "asc", label: "ચડતો ક્રમ" },
  { value: "desc", label: "ઉતરતો ક્રમ" },
];

const TurningReceivesPage = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const receives = useAppSelector(getTurningReceives);
  const totalReceives = useAppSelector(getTotalTurningReceives);
  const partyOptions = useAppSelector(getTurningReceiveFilterPartyOptions);
  const loggingUser = useAppSelector(getLoggingUser);
  const savedList = useAppSelector((state) => getListState(state, listKey));

  const canCreate = hasPermission(
    loggingUser?.permissions,
    permission.turningReceivesCreate
  );
  const canReport = hasPermission(
    loggingUser?.permissions,
    permission.turningReceivesReport
  );

  // The filters in use when the user last left the list (to open a record, say), else none.
  const savedFilters: Filters = savedList.filters || noFilters;

  const { getValues, handleSubmit, register, reset } =
    useForm<FilterFormInput>({
      defaultValues: {
        search: savedFilters.search,
        fromDate: savedFilters.fromDate,
        toDate: savedFilters.toDate,
      },
    });

  const [page, setPage] = useState<number>(savedList.page || 1);
  const [pageSize, setPageSize] = useState<number>(savedList.pageSize || 50);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isReportLoading, setIsReportLoading] = useState<boolean>(false);
  const [showFilter, setShowFilter] = useState<boolean>(
    !!savedList.showFilter
  );
  // The filter card's selects; like its inputs, they count only once applied.
  const [draft, setDraft] = useState<SelectFilters>(() =>
    selectsOf(savedFilters)
  );
  // The filters in use.
  const [filters, setFilters] = useState<Filters>(savedFilters);
  const [reloadKey, setReloadKey] = useState<number>(0);

  const fetchId = useRef(0);

  const setDraftValue = <K extends keyof SelectFilters>(
    key: K,
    value: SelectFilters[K]
  ) => setDraft((current) => ({ ...current, [key]: value }));

  useEffect(() => {
    dispatch(fetchTurningReceiveFilterParties());
  }, [dispatch]);

  // Coming back to the list (from an order, say) shows the same page and filters.
  useEffect(() => {
    dispatch(
      saveListState({
        key: listKey,
        value: { page, pageSize, showFilter, filters },
      })
    );
  }, [dispatch, page, pageSize, showFilter, filters]);

  useEffect(() => {
    const currentFetch = ++fetchId.current;

    setIsLoading(true);

    dispatch(
      fetchTurningReceiveListData({
        page,
        size: pageSize,
        search: filters.search || null,
        fromDate: filters.fromDate || null,
        toDate: filters.toDate || null,
        party_id: filters.party_id,
        date_by: filters.date_by,
        sort_by: filters.sort_by,
        sort_order: filters.sort_order,
      })
    ).then((action: any) => {
      const result = action?.payload?.data;

      if (currentFetch === fetchId.current && result?.status !== "success") {
        toast.error(
          result?.message || "કંઈક ખોટું ગયું, કૃપા કરીને ફરીથી પ્રયત્ન કરો.",
          { ...(toastStyles as any) }
        );
      }

      // A saved page that is now past the end (its rows deleted, say) moves to the last page.
      const lastPage = Math.max(
        1,
        Math.ceil((Number(result?.data?.total_records) || 0) / pageSize)
      );

      if (
        currentFetch === fetchId.current &&
        page > 1 &&
        result?.status === "success" &&
        !result?.data?.records?.length &&
        lastPage < page
      ) {
        setPage(lastPage);
        return;
      }

      setIsLoading(false);
    });
  }, [dispatch, page, pageSize, filters, reloadKey]);

  const handlePageSizeChange = (size: number) => {
    setPageSize(size);
    setPage(1);
  };

  const handleOnFilterSubmit = (data: FilterFormInput) => {
    setFilters({
      search: data.search,
      fromDate: data.fromDate,
      toDate: data.toDate,
      ...draft,
    });
    setPage(1);
  };

  const handleClearFilter = () => {
    reset({ search: "", fromDate: "", toDate: "" });
    setDraft(defaultSelects);
    setFilters(noFilters);
    setPage(1);
  };

  // A PDF of every receive the filter card finds, using its values as filled in (applied or not). Rates and amounts
  // are in it only for a user who may see rates.
  const handleReportExport = async () => {
    const data = getValues();

    if (!checkReportRange(data.fromDate, data.toDate)) {
      return;
    }

    setIsReportLoading(true);
    const file = await makeReport(`/v1/admin/turning-receives/report`, {
      search: data.search || null,
      fromDate: data.fromDate,
      toDate: data.toDate,
      ...draft,
    });
    setIsReportLoading(false);

    if (file) dispatch(updatePdfFile(file));
  };

  const handleDeleteReceive = async (id: number) => {
    if (await deleteTurningReceive(id)) {
      // Step back a page when its last row was deleted.
      if (receives.length > 1 || page === 1) {
        setReloadKey((key) => key + 1);
      } else {
        setPage(page - 1);
      }
    }
  };

  return (
    <AppLayout>
      <div className="mb-2 md:mb-0">
        <Breadcrumb items={breadcrumbItems} />
      </div>

      <div className="flex items-center mb-4 md:mb-6 mt-2">
        <h1 className={pageTitleClass}>ટર્નિંગ રિસીવ</h1>
      </div>

      <div className="space-y-4 md:space-y-6">
        <div className={actionBarEndClass}>
          <div
            className={cx(
              "grid w-full gap-2 sm:flex sm:w-auto",
              canCreate ? "grid-cols-2" : "grid-cols-1"
            )}
          >
            <Button
              variant="outline"
              onClick={() => setShowFilter(!showFilter)}
            >
              <LuFilter className="h-4 w-4" />
              ફિલ્ટર
            </Button>

            {canCreate && (
              <Button
                variant="primary"
                onClick={() => navigate(route.turningReceiveCreate)}
              >
                <LuPlus className="h-4 w-4" />
                નવી રિસીવ ઉમેરો
              </Button>
            )}
          </div>
        </div>

        {showFilter && (
          <div className={cardClass}>
            <div className={cardHeaderClass}>
              <div className={`${cardTitleClass} text-lg`}>ફિલ્ટર</div>
            </div>

            <div className={cardContentClass}>
              <form onSubmit={handleSubmit(handleOnFilterSubmit)}>
                <div className={filterGridClass}>
                  <div className="space-y-2">
                    <label className={filterLabelClass}>શોધો</label>
                    <div className="relative">
                      <LuSearch className={fieldIconClass} />
                      <Input
                        placeholder="ID અથવા આઈટમ કોડ દ્વારા શોધો"
                        className={fieldInputClass()}
                        {...register("search")}
                      />
                    </div>
                  </div>

                  <FilterSelect
                    label="પાર્ટી"
                    icon={LuBuilding2}
                    placeholder="પાર્ટી સિલેક્ટ કરો"
                    options={partyOptions}
                    value={draft.party_id}
                    onChange={(value) => setDraftValue("party_id", value)}
                    isClearable
                    isSearchable
                  />

                  <div className="space-y-2">
                    <label className={filterLabelClass}>તારીખ થી</label>
                    <div className="relative">
                      <LuDatabase className={fieldIconClass} />
                      <Input
                        type="date"
                        className={fieldInputClass()}
                        {...register("fromDate")}
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <label className={filterLabelClass}>તારીખ સુધી</label>
                    <div className="relative">
                      <LuDatabase className={fieldIconClass} />
                      <Input
                        type="date"
                        className={fieldInputClass()}
                        {...register("toDate")}
                      />
                    </div>
                  </div>

                  <FilterSelect
                    label="તારીખ ફિલ્ટર"
                    icon={LuCalendarClock}
                    placeholder="તારીખ સિલેક્ટ કરો"
                    options={dateByOptions}
                    value={draft.date_by}
                    onChange={(value) => value && setDraftValue("date_by", value)}
                  />

                  <FilterSelect
                    label="ગોઠવો"
                    icon={LuArrowUpDown}
                    placeholder="ગોઠવણી સિલેક્ટ કરો"
                    options={sortByOptions}
                    value={draft.sort_by}
                    onChange={(value) => value && setDraftValue("sort_by", value)}
                  />

                  <FilterSelect
                    label="ક્રમ"
                    icon={LuArrowDownWideNarrow}
                    placeholder="ક્રમ સિલેક્ટ કરો"
                    options={sortOrderOptions}
                    value={draft.sort_order}
                    onChange={(value) =>
                      value && setDraftValue("sort_order", value)
                    }
                  />
                </div>

                <div className={cx(filterFooterClass, "flex-wrap")}>
                  <Button
                    type="button"
                    variant="outline"
                    className="max-sm:flex-1"
                    onClick={handleClearFilter}
                  >
                    ફિલ્ટર સાફ કરો
                  </Button>

                  {canReport && (
                    <Button
                      type="button"
                      variant="primary"
                      className="max-sm:flex-1"
                      disabled={isReportLoading}
                      onClick={handleReportExport}
                    >
                      {isReportLoading
                        ? "રિપોર્ટ બની રહ્યો છે..."
                        : "રિપોર્ટ એક્સપોર્ટ"}
                    </Button>
                  )}

                  <Button
                    type="submit"
                    variant="primary"
                    className="max-sm:flex-1"
                  >
                    ફિલ્ટર લાગુ કરો
                  </Button>
                </div>
              </form>
            </div>
          </div>
        )}

        <TurningReceiveList
          receives={receives}
          pageNumber={page}
          pageSize={pageSize}
          isLoading={isLoading}
          handleDeleteReceive={handleDeleteReceive}
        />

        {!isLoading && receives.length > 0 && (
          <Pagination
            itemsPerPage={pageSize}
            totalItems={totalReceives}
            paginate={setPage}
            currentPage={page}
            onPageSizeChange={handlePageSizeChange}
            itemLabel="ટર્નિંગ રિસીવ"
          />
        )}
      </div>
    </AppLayout>
  );
};
export default TurningReceivesPage;
